import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  ApprovedStatusType,
  Availability,
  DayPortion,
  Expense,
  LEAVE_ENTITLEMENT_DAY_DECIMAL_PRECISION,
  LEAVE_REQUEST_DAY_DECIMAL_PRECISION,
  LeaveRequest,
  MIN_HOURLY_LEAVE_MINUTES,
  Status,
  WORK_HOURS_PER_DAY,
} from '@hr-outsourcing/shared';
import type { TFunction } from 'i18next';
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Maps an ISO currency code to its display symbol, falling back to the code
// itself for anything unmapped. Used wherever a price is rendered so the symbol
// follows the record's own currency instead of a hardcoded one.
export function getCurrencySymbol(currency: string): string {
  const map: Record<string, string> = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };
  return map[currency] ?? currency;
}
export function getAvailabilityVariant(availability: Availability) {
  switch (availability) {
    case Availability.AVAILABLE:
      return 'default';
    case Availability.ON_LEAVE:
      return 'destructive';
    default:
      return 'outline';
  }
}
export const getStatusBadgeClasses = (status: ApprovedStatusType) => {
  const base = 'border bg-transparent';
  switch (status) {
    case ApprovedStatusType.APPROVED:
      return `${base} border-success/40 text-success bg-success/10`;
    case ApprovedStatusType.PENDING:
      return `${base} border-warning/40 text-warning bg-warning/10`;
    case ApprovedStatusType.REJECTED:
    case ApprovedStatusType.REVOKED:
    case ApprovedStatusType.CANCELLED:
      return `${base} border-status-error/40 text-status-error bg-status-error-bg`;
    default:
      return `${base} border-muted text-muted-foreground bg-muted`;
  }
};
const SOFT_LEAVE_STATUS_CLASSES: Record<ApprovedStatusType, string> = {
  [ApprovedStatusType.PENDING]: 'border border-status-pending/30 bg-status-pending-bg text-status-pending',
  [ApprovedStatusType.APPROVED]: 'border border-status-active/30 bg-status-active-bg text-status-active',
  [ApprovedStatusType.REJECTED]: 'border border-status-error/30 bg-status-error-bg text-status-error',
  [ApprovedStatusType.CANCELLED]: 'border border-status-inactive/30 bg-status-inactive-bg text-status-inactive',
  [ApprovedStatusType.REVOKED]: 'border border-status-inactive/30 bg-status-inactive-bg text-status-inactive',
  [ApprovedStatusType.REVOKE_PENDING_HR_APPROVAL]:
    'border border-status-pending/30 bg-status-pending-bg text-status-pending',
  [ApprovedStatusType.REVOKE_PENDING_EMPLOYEE_APPROVAL]:
    'border border-status-pending/30 bg-status-pending-bg text-status-pending',
};
export const getSoftLeaveStatusClasses = (status: ApprovedStatusType) =>
  SOFT_LEAVE_STATUS_CLASSES[status] ?? 'border border-border bg-muted/40 text-muted-foreground';
const EXPENSE_STATUS_CLASS_MAP: Record<string, string> = {
  PENDING: 'border border-status-pending/30 bg-status-pending-bg text-status-pending',
  APPROVED: 'border border-status-active/30 bg-status-active-bg text-status-active',
  REJECTED: 'border border-status-error/30 bg-status-error-bg text-status-error',
  CANCELLED: 'border border-status-inactive/30 bg-status-inactive-bg text-status-inactive',
  REIMBURSED: 'border border-status-active/30 bg-status-active-bg text-status-active',
};
export const getExpenseStatusClasses = (status: Expense['status']) =>
  EXPENSE_STATUS_CLASS_MAP[status] ?? 'border border-border bg-muted/40 text-muted-foreground';
const WORKING_HOURS_STATUS_CLASS_MAP: Record<ApprovedStatusType, string> = {
  [ApprovedStatusType.PENDING]: 'border border-status-pending/30 bg-status-pending-bg text-status-pending',
  [ApprovedStatusType.APPROVED]: 'border border-status-active/30 bg-status-active-bg text-status-active',
  [ApprovedStatusType.REJECTED]: 'border border-status-error/30 bg-status-error-bg text-status-error',
  [ApprovedStatusType.CANCELLED]: 'border border-status-inactive/30 bg-status-inactive-bg text-status-inactive',
  [ApprovedStatusType.REVOKED]: 'border border-status-inactive/30 bg-status-inactive-bg text-status-inactive',
  [ApprovedStatusType.REVOKE_PENDING_HR_APPROVAL]:
    'border border-status-pending/30 bg-status-pending-bg text-status-pending',
  [ApprovedStatusType.REVOKE_PENDING_EMPLOYEE_APPROVAL]:
    'border border-status-pending/30 bg-status-pending-bg text-status-pending',
};
export const getWorkingHoursStatusClasses = (status: ApprovedStatusType) =>
  WORKING_HOURS_STATUS_CLASS_MAP[status] ?? 'border border-border bg-muted/40 text-muted-foreground';
const normalizeDate = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};
const isWeekend = (date: Date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};
export const hasFridayMondaySpan = (startDate: string | Date, endDate: string | Date): boolean => {
  const start = normalizeDate(new Date(startDate));
  const end = normalizeDate(new Date(endDate));
  if (end < start) return false;
  const firstFriday = new Date(start);
  const offsetToFriday = (5 - firstFriday.getDay() + 7) % 7;
  firstFriday.setDate(firstFriday.getDate() + offsetToFriday);
  for (let friday = firstFriday; friday <= end; friday.setDate(friday.getDate() + 7)) {
    const monday = new Date(friday);
    monday.setDate(monday.getDate() + 3);
    if (monday <= end) return true;
  }
  return false;
};
const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};
const MINUTES_PER_HOUR = 60;
const LEAVE_DAY_MINUTES = WORK_HOURS_PER_DAY * MINUTES_PER_HOUR;
const parseNumericValue = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const calculateHourlyMinutes = (startTime: string, endTime: string): number =>
  Math.max(0, parseTimeToMinutes(endTime) - parseTimeToMinutes(startTime));

export const calculateHourlyDays = (startTime: string, endTime: string): number => {
  const hours = calculateHourlyMinutes(startTime, endTime) / 60;
  return Math.max(0, Number((hours / WORK_HOURS_PER_DAY).toFixed(LEAVE_REQUEST_DAY_DECIMAL_PRECISION)));
};

export const splitLeaveDaysToDuration = (
  leaveDays: number | string | null | undefined,
  options?: { allowNegative?: boolean }
) => {
  const roundedTotalMinutes =
    Math.round((parseNumericValue(leaveDays) * LEAVE_DAY_MINUTES) / MIN_HOURLY_LEAVE_MINUTES) *
    MIN_HOURLY_LEAVE_MINUTES;
  const totalMinutes = options?.allowNegative ? roundedTotalMinutes : Math.max(0, roundedTotalMinutes);
  const absoluteTotalMinutes = Math.abs(totalMinutes);
  const sign = totalMinutes < 0 ? -1 : 1;
  const days = Math.floor(absoluteTotalMinutes / LEAVE_DAY_MINUTES) * sign;
  const remainingMinutes = absoluteTotalMinutes % LEAVE_DAY_MINUTES;
  const hours = Math.floor(remainingMinutes / MINUTES_PER_HOUR);
  const minutes = remainingMinutes % MINUTES_PER_HOUR;

  return { days, hours, minutes, totalMinutes };
};

export const combineLeaveDayTime = (
  days: number,
  hours: number,
  minutes = 0,
  options?: { allowNegative?: boolean }
): number => {
  const durationMinutes = Math.abs(days) * LEAVE_DAY_MINUTES + hours * MINUTES_PER_HOUR + minutes;
  const signedTotalMinutes = days < 0 ? -durationMinutes : durationMinutes;
  const totalMinutes = options?.allowNegative ? signedTotalMinutes : Math.max(0, signedTotalMinutes);
  return Number((totalMinutes / LEAVE_DAY_MINUTES).toFixed(LEAVE_ENTITLEMENT_DAY_DECIMAL_PRECISION));
};

// Client-only formatter: uses i18n labels for rendered UI text.
export const formatLeaveDaysAsDayHours = (
  leaveDays: number | string | null | undefined,
  translate: TFunction
): string => {
  const numericValue = parseNumericValue(leaveDays);
  const isNegative = numericValue < 0;
  const { days, hours, minutes } = splitLeaveDaysToDuration(isNegative ? -numericValue : numericValue);
  const parts: string[] = [];

  if (days > 0) {
    parts.push(
      translate('leaves.table.dailyDuration', {
        count: days,
        defaultValue: days === 1 ? `${days} day` : `${days} days`,
      })
    );
  }

  if (hours > 0) {
    parts.push(
      `${hours} ${translate('leaves.table.hourUnit', {
        count: hours,
        defaultValue: hours === 1 ? 'hour' : 'hours',
      })}`
    );
  }

  if (minutes > 0) {
    parts.push(
      `${minutes} ${translate('leaves.table.minuteUnit', {
        count: minutes,
        defaultValue: minutes === 1 ? 'minute' : 'minutes',
      })}`
    );
  }

  if (parts.length === 0) {
    return translate('leaves.table.dailyDuration', { count: 0, defaultValue: '0 days' });
  }

  return isNegative ? `-${parts.join(' ')}` : parts.join(' ');
};

export const formatHourlyLeaveDuration = (totalMinutes: number, translate: TFunction): string => {
  const minutesValue = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(minutesValue / 60);
  const minutes = minutesValue % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(
      `${hours} ${translate('leaves.table.hourUnit', {
        count: hours,
        defaultValue: hours === 1 ? 'hour' : 'hours',
      })}`
    );
  }

  if (minutes > 0 || parts.length === 0) {
    parts.push(
      `${minutes} ${translate('leaves.table.minuteUnit', {
        count: minutes,
        defaultValue: minutes === 1 ? 'minute' : 'minutes',
      })}`
    );
  }

  return parts.join(' ');
};

export const calculateDays = (
  startDate: string | Date,
  endDate: string | Date,
  dayPortion: LeaveRequest['dayPortion']
) => {
  if (dayPortion !== DayPortion.ALL_DAY) return 0.5;
  const start = normalizeDate(new Date(startDate));
  const end = normalizeDate(new Date(endDate));
  if (end < start) return 0;
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    if (!isWeekend(current)) {
      count += 1;
    }
    current.setDate(current.getDate() + 1);
  }
  const firstFriday = new Date(start);
  const offsetToFriday = (5 - firstFriday.getDay() + 7) % 7;
  firstFriday.setDate(firstFriday.getDate() + offsetToFriday);
  for (let friday = firstFriday; friday <= end; friday.setDate(friday.getDate() + 7)) {
    const monday = new Date(friday);
    monday.setDate(monday.getDate() + 3);
    if (monday <= end) {
      count += 1;
    }
  }
  return count;
};
export type DayPortionValue = DayPortion;

export interface PerDayEntry {
  date: Date;
  portion: DayPortionValue;
}

export const calculateDailyLeaveDurationWithPortions = (
  startDate: string | Date,
  endDate: string | Date,
  dayPortion: LeaveRequest['dayPortion'],
  startDayPortion?: DayPortionValue,
  endDayPortion?: DayPortionValue
): number => {
  const start = normalizeDate(new Date(startDate));
  const end = normalizeDate(new Date(endDate));
  if (start.getTime() === end.getTime()) {
    const effectivePortion =
      dayPortion !== DayPortion.ALL_DAY
        ? dayPortion
        : startDayPortion && startDayPortion !== DayPortion.ALL_DAY
          ? startDayPortion
          : endDayPortion && endDayPortion !== DayPortion.ALL_DAY
            ? endDayPortion
            : DayPortion.ALL_DAY;
    return calculateDays(startDate, endDate, effectivePortion);
  }
  // Multi-day: always use ALL_DAY as base to avoid double-deduction when dayPortion is not ALL_DAY
  const base = calculateDays(startDate, endDate, DayPortion.ALL_DAY);
  const startDed = startDayPortion && startDayPortion !== DayPortion.ALL_DAY && !isWeekend(start) ? 0.5 : 0;
  const endDed = endDayPortion && endDayPortion !== DayPortion.ALL_DAY && !isWeekend(end) ? 0.5 : 0;
  return Math.max(0, base - startDed - endDed);
};

export const resolveDailyLeaveDuration = (
  durationInDays: number | string | null | undefined,
  calculatedDuration: number
): number => {
  const storedDuration = parseNumericValue(durationInDays);
  return storedDuration > 0 ? storedDuration : calculatedDuration;
};

export function buildPerDayBreakdown(
  startDate: Date,
  endDate: Date,
  startDayPortion: DayPortionValue,
  endDayPortion: DayPortionValue
): PerDayEntry[] {
  const entries: PerDayEntry[] = [];
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  const current = new Date(start);
  while (current <= end) {
    const dow = current.getDay();
    if (dow !== 0 && dow !== 6) {
      const isFirst = current.getTime() === start.getTime();
      const isLast = current.getTime() === end.getTime();
      let portion: DayPortionValue = DayPortion.ALL_DAY;
      if (isFirst) {
        portion = startDayPortion;
      } else if (isLast) {
        portion = endDayPortion;
      }
      entries.push({ date: new Date(current), portion });
    }
    current.setDate(current.getDate() + 1);
  }
  return entries;
}

export const isAfterToday = (dateLike?: string | Date | null) => {
  if (!dateLike) return false;
  const d = new Date(dateLike);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d > today;
};
export function decodeToken(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};
export const formatDuration = (totalMinutes: number, locale = 'tr'): string => {
  const isTurkish = locale.toLowerCase().startsWith('tr');
  const hourUnit = isTurkish ? 's' : 'h';
  const minuteUnit = isTurkish ? 'dk' : 'm';

  if (!totalMinutes || totalMinutes <= 0) {
    return `0${minuteUnit}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}${hourUnit}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}${minuteUnit}`);
  }

  return parts.length > 0 ? parts.join(' ') : `0${minuteUnit}`;
};
export const getStatusVariant = (status: string) => {
  switch (status) {
    case Status.ACTIVE:
      return 'default';
    case Status.ON_HOLD:
      return 'secondary';
    case Status.CLOSED:
      return 'destructive';
    default:
      return 'outline';
  }
};
export const getPositionStatusLabel = (status: string | undefined, translate: TFunction) => {
  if (!status) return '-';
  const statusKey = status.toUpperCase();
  return translate(`positions.statuses.${statusKey}`, { defaultValue: status });
};
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
export const formatStartDateToISO = (dateString: string | undefined): string | undefined => {
  if (!dateString) return undefined;
  const date = new Date(dateString + 'T00:00:00.000Z');
  return date.toISOString();
};
export const formatEndDateToISO = (dateString: string | undefined): string | undefined => {
  if (!dateString) return undefined;
  const date = new Date(dateString + 'T23:59:59.999Z');
  return date.toISOString();
};
export const formatDateTimeToIsoWithOffset = (date: Date): string => {
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const timezoneOffsetMinutes = -date.getTimezoneOffset();
  const sign = timezoneOffsetMinutes >= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(timezoneOffsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, '0');
  const offsetMinutes = String(absoluteOffsetMinutes % 60).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`;
};
export const combineLocalDateAndTimeToIsoWithOffset = (dateString: string, timeString: string): string => {
  const [year, month, day] = dateString.split('-').map(Number);
  const [hours, minutes] = timeString.split(':').map(Number);
  const localDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);

  return formatDateTimeToIsoWithOffset(localDateTime);
};
export const formatDateToDDMMYYYY = (date: string | Date | number | null | undefined): string => {
  if (!date) return '';
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
};

export const fetchingClass = (isFetching: boolean) =>
  isFetching ? 'transition-opacity opacity-70' : 'transition-opacity';
